import {jsx} from 'panel';

import {AsyncButtonEl} from 'common/components/async-button';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {TooltipEl} from 'common/components/tooltip';
import {ButtonTheme} from 'common/components/button/constants';
import {SIZE, TextHighlightEl, THEME} from 'common/components/text-highlight';
import {Placement} from 'common/components/tooltip/types';
import * as colors from 'common/util/colors';
import {getHost} from 'common/util/url';
import {CopyToClipboardButtonEl} from 'common/widgets/copy-to-clipboard/copy-to-clipboard-button';
import {CopyType} from 'common/widgets/copy-to-clipboard/copy-to-clipboard-button/types';
import {PublicDashboardPasswordRowEl} from 'common/widgets/public-dashboard-password-row';

export enum OverflowActions {
  Deactivate = `Deactivate`,
  Edit = `Edit Passcode`,
}

export const getPublicDashboardLinkOverflowButton = ({workspaceId, dashboardId, canEdit}, changeHandler) => {
  return (
    <>
      <SelectEl
        attrs={{
          theme: SelectTheme.Secondary,
          elref: `publicDashboardLinkOverflow_${workspaceId}`,
          icon: {leftIcon: `hor-ellipsis`},
          options: [OverflowActions.Edit, OverflowActions.Deactivate],
          'icon-only': true,
          'menu-width': 125,
          disabled: !canEdit,
        }}
        on={{change: (ev) => changeHandler(ev, {dashboardId, workspaceId})}}
      />
      {!canEdit && (
        <TooltipEl
          attrs={{
            'max-width': `300px`,
          }}
        >
          You don't have access to edit this board or links
        </TooltipEl>
      )}
    </>
  );
};

export const getPublicDashboardLink = ({publicLink, passwordProtected}) => {
  const href = `/p/${publicLink}`;
  const fullHref = `${getHost()}${href}`;
  return (
    <div sel='.public-dashboard-link-mixin'>
      <span sel='.public-dashboard-link-container'>
        <a sel='.public-dashboard-link' attrs={{href, target: `_blank`}}>
          {`p/${publicLink}`}
        </a>
        {passwordProtected && <div sel='.public-dashboard-link-subtext'> passcode protected </div>}
        <TooltipEl
          attrs={{
            'max-width': `400px`,
          }}
        >
          {fullHref}
        </TooltipEl>
      </span>
      <CopyToClipboardButtonEl
        attrs={{value: fullHref, theme: ButtonTheme.Tertiary, 'copy-type': CopyType.Link}}
        sel='.public-dashboard-link-copy-btn'
      />
    </div>
  );
};

export const getPublicDashboardLinkDeactivateButton = ({workspaceId, canDeactivate}, deleteCallback) => {
  return (
    <>
      <AsyncButtonEl
        params={{
          asyncAction: () => deleteCallback(workspaceId.toString()),
          theme: ButtonTheme.Secondary,
          elref: `publicDashboardLinkDeactivate_${workspaceId}`,
          icon: `trashcan`,
          disabled: !canDeactivate,
        }}
      />
      <TooltipEl
        attrs={{
          'max-width': `300px`,
        }}
      >
        {canDeactivate ? `Deactivate Public Link` : `You don't have access to edit this board or links`}
      </TooltipEl>
    </>
  );
};

export const getPublicDashboardTextHighlight = (clickHandler) => {
  return (
    <TextHighlightEl
      attrs={{
        elref: `publicDashboardTextHighlight`,
        text: `Public`,
        size: SIZE.SMALL,
        theme: THEME.INFORMATION,
      }}
    >
      <TooltipEl attrs={{placement: Placement.Right, 'max-width': `233px`, offset: 10}}>
        <div>
          Public Boards are accessible to anyone with a URL. People outside your project will see see a read-only
          version.
        </div>
        <br />
        <div
          sel='.view-public-links'
          on={{
            click: (ev) => {
              ev.preventDefault();
              clickHandler();
            },
          }}
          style={{color: colors.gray3, cursor: `pointer`, textDecoration: `underline`}}
        >
          View public links for this Board
        </div>
      </TooltipEl>
    </TextHighlightEl>
  );
};

export const LinkDetailMixin = (onPasswordChange) => {
  return (row) => {
    return (
      <PublicDashboardPasswordRowEl
        attrs={{
          'workspace-id': row.workspaceId,
          'public-link-id': row.publicLink,
          'password-protected': row.passwordProtected,
          'public-link-embed-link-with-passcode': row.embedLink,
        }}
        on={{passwordChange: onPasswordChange}}
        key={row.publicLink}
      />
    );
  };
};
